import { ERegionCode } from '@sureapp/platform-sdk';
import { isFunction } from '@sureapp/wl-toggle-common/dist/services/envTypes';
import { mergeObjectsButOverrideArrays } from '@sureapp/wl-toggle-common/dist/utils';
import clonedeep from 'lodash/cloneDeep';
import merge from 'lodash/merge';

import type { TConfig, TConfigInput } from '@@/auto/interfaces';

import { overrideObjectsAndArrays, overrideFunctions } from './mergeHelpers';

export function buildFunctionalConfig({
  defaultConfig,
  functionalConfig,
}: {
  defaultConfig: TConfig;
  functionalConfig: DeepPartial<TConfigInput>;
}) {
  const mergedConfig = merge(
    {},
    clonedeep(defaultConfig),
    clonedeep(functionalConfig)
  );
  const overriddenConfig = overrideFunctions(mergedConfig, functionalConfig);

  Object.entries(functionalConfig).forEach(([key, possibleFn]) => {
    if (isFunction(possibleFn)) {
      overriddenConfig[key as keyof TConfig] = possibleFn(
        defaultConfig[key as keyof TConfig]
      );
    }
  });

  return overriddenConfig;
}

/*
 * regionConfigMerge concats `visibleCoverages` arrays unless otherwise specified
 * in a regionalConfig's `overrides` array. It initially combines all coverages,
 * then overrides coverages via the `overridenCoverages` reducer function below.
 */

export default function regionConfigMerge({
  defaultConfig,
  newConfig,
}: {
  defaultConfig: TConfig;
  newConfig: DeepPartial<TConfig> | DeepPartial<TConfigInput>;
  regionCode?: ERegionCode;
}): TConfig {
  const overrideConfig: DeepPartial<TConfig> = {
    ...newConfig,
    auto: isFunction(newConfig.auto)
      ? newConfig.auto(defaultConfig.auto)
      : newConfig.auto,
    discountsConfig: isFunction(newConfig.discountsConfig)
      ? newConfig.discountsConfig(defaultConfig.discountsConfig)
      : newConfig.discountsConfig,
    driverFormConfig: isFunction(newConfig.driverFormConfig)
      ? newConfig.driverFormConfig(defaultConfig.driverFormConfig)
      : newConfig.driverFormConfig,
    visibleCoverages: isFunction(newConfig.visibleCoverages)
      ? newConfig.visibleCoverages(defaultConfig.visibleCoverages)
      : newConfig.visibleCoverages,
    coverage: isFunction(newConfig.coverage)
      ? newConfig.coverage(defaultConfig.coverage)
      : newConfig.coverage,
    coverageConfig: isFunction(newConfig.coverageConfig)
      ? newConfig.coverageConfig(defaultConfig.coverageConfig)
      : newConfig.coverageConfig,
  } as DeepPartial<TConfig>;

  if (isFunction(newConfig.auto) || isFunction(newConfig.coverage)) {
    return overrideObjectsAndArrays(defaultConfig, overrideConfig);
  }
  return mergeObjectsButOverrideArrays<TConfig>(defaultConfig, overrideConfig);
}
